<template>
  <div class="property-owner-selector">
    <!-- Dashboard Header for logout functionality -->
    <DashboardHeader :currentUser="currentUser" />

    <div class="container is-widescreen">
      <div class="content-wrapper">
        <!-- Header -->
        <div class="header-section">
          <h1 class="title is-2 has-text-centered">Select Property Owner</h1>
          <p class="subtitle is-5 has-text-centered medium-gray">
            Choose a property owner to submit applications on their behalf
          </p>
        </div>

        <!-- Table Section -->
        <div class="box">
          <!-- Header with search -->
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <h3 class="title is-4">Property Owners</h3>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item">
                <div class="field" style="min-width: 300px;">
                  <div class="control has-icons-left">
                    <input
                      v-model="searchTerm"
                      class="input"
                      type="text"
                      placeholder="Search by any of the columns..."
                      style="height: 40px;"
                    />
                    <span class="icon is-left">
                      <i class="fas fa-search"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Table -->
          <div class="table-container">
            <table class="table is-fullwidth is-hoverable">
              <thead>
                <tr>
                  <th></th>
                  <th>Organization</th>
                  <th>Names</th>
                  <th>Emails</th>
                  <th>Phones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="owner in filteredOwners" :key="owner.rid">
                  <td>
                    <span class="icon eye-icon" @click="selectOwner(owner)">
                      <i class="fas fa-eye"></i>
                    </span>
                  </td>
                  <td><strong>{{ owner.property_owner_org_name || '-' }}</strong></td>
                  <td class="stacked-cell">
                    <template v-if="owner.property_owner_combined_information && owner.property_owner_combined_information.length">
                      <div v-for="(info, idx) in owner.property_owner_combined_information" :key="idx" class="stacked-item">
                        {{ info.name || '-' }}
                      </div>
                    </template>
                    <template v-else>-</template>
                  </td>
                  <td class="stacked-cell">
                    <template v-if="owner.property_owner_combined_information && owner.property_owner_combined_information.length">
                      <div v-for="(info, idx) in owner.property_owner_combined_information" :key="idx" class="stacked-item">
                        {{ info.email || '-' }}
                      </div>
                    </template>
                    <template v-else>-</template>
                  </td>
                  <td class="stacked-cell">
                    <template v-if="owner.property_owner_combined_information && owner.property_owner_combined_information.length">
                      <div v-for="(info, idx) in owner.property_owner_combined_information" :key="idx" class="stacked-item">
                        {{ info.phone || '-' }}
                      </div>
                    </template>
                    <template v-else>-</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- No results message -->
          <div v-if="filteredOwners.length === 0" class="has-text-centered" style="padding: 3rem;">
            <p class="medium-gray">
              <i class="fas fa-users fa-3x" style="color: #cfcfcf; margin-bottom: 1rem;"></i><br>
              <strong>No property owners found</strong><br>
              Try adjusting your search criteria
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DashboardHeader from "./DashboardHeader.vue";

export default {
  name: "PropertyOwnerSelector",
  components: {
    DashboardHeader,
  },
  data() {
    return {
      searchTerm: "",
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters("auth", ["userInfo"]),
    ...mapGetters("dashboard", ["activePropertyOwners"]),
    currentUser() {
      return this.userInfo;
    },
    filteredOwners() {
      if (!this.searchTerm) {
        return this.activePropertyOwners;
      }
      const term = this.searchTerm.toLowerCase();
      return this.activePropertyOwners.filter((owner) => {
        // Search by organization name
        if (owner.property_owner_org_name?.toLowerCase().includes(term)) {
          return true;
        }
        // Search in combined information (names and emails)
        if (owner.property_owner_combined_information) {
          return owner.property_owner_combined_information.some(
            (info) =>
              info.name?.toLowerCase().includes(term) ||
              info.email?.toLowerCase().includes(term) ||
              info.phone?.toLowerCase().includes(term)
          );
        }
        return false;
      });
    },
  },
  async created() {
    await this.loadPropertyOwners();
  },
  methods: {
    ...mapActions("dashboard", ["fetchPropertyOwners"]),
    async loadPropertyOwners() {
      this.isLoading = true;
      try {
        await this.fetchPropertyOwners();
      } catch (error) {
        console.error("Error loading property owners:", error);
      } finally {
        this.isLoading = false;
      }
    },
    selectOwner(owner) {
      console.log("Selected property owner:", owner);
      this.$emit("select", owner);
    },
  },
};
</script>

<style scoped>
/* Full screen layout */
.property-owner-selector {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: #f0f0f0; /* Ghost Gray - Philly Style Guide */
  overflow-y: auto;
  padding-top: 70px; /* Account for fixed header */
  padding-left: 1rem;
  padding-right: 1rem;
  padding-bottom: 3rem;
}

.content-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  padding-top: 2rem;
}

/* Header Section */
.header-section {
  margin-bottom: 2rem;
}

.medium-gray {
  color: #a1a1a1; /* Medium Gray - Philly Style Guide */
}

/* Eye icon styling */
.eye-icon {
  cursor: pointer;
  color: #2176d2; /* Ben Franklin Blue - Philly Style Guide */
  transition: color 0.2s ease;
}

.eye-icon:hover {
  color: #0f4d90; /* Dark Ben Franklin Blue - Philly Style Guide */
}

/* Stacked cell styling for multiple owners */
.stacked-cell {
  vertical-align: top;
}

.stacked-item {
  padding: 0.15rem 0;
  border-bottom: 1px solid #f0f0f0; /* Ghost Gray - Philly Style Guide */
  line-height: 1.4;
}

.stacked-item:last-child {
  border-bottom: none;
}

/* Responsive */
@media (max-width: 768px) {
  .property-owner-selector {
    padding-top: 60px; /* Smaller header on mobile */
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    padding-bottom: 2rem;
  }

  .level {
    flex-direction: column;
    gap: 1rem;
  }

  .level-right {
    width: 100%;
  }

  .level-item .field {
    width: 100%;
    min-width: unset !important;
  }
}
</style>
